import type { Votekick, VotekickStatus } from "../contracts";
import type { VotekickAdmissionResult, VotekickRepository } from "../repository";

interface VotekickRow {
  votekick_id: string;
  target_user_id: string | null;
  target_login: string | null;
  initiator_user_id: string | null;
  status: VotekickStatus;
  threshold: number;
  yes_votes: number;
  no_votes: number;
  ballot_revision: number;
  duration_seconds: number | null;
  started_at: string;
  expires_at: string;
  ended_at: string | null;
  lifted_at: string | null;
  lift_claimed_at: string | null;
}

const mapRow = (row: VotekickRow): Votekick => ({
  id: row.votekick_id,
  targetUserId: row.target_user_id,
  targetLogin: row.target_login,
  initiatorUserId: row.initiator_user_id,
  status: row.status,
  threshold: row.threshold,
  yesVotes: row.yes_votes,
  noVotes: row.no_votes,
  ballotRevision: row.ballot_revision,
  durationSeconds: row.duration_seconds,
  startedAt: row.started_at,
  endsAt: row.expires_at,
  endedAt: row.ended_at,
  liftedAt: row.lifted_at,
  liftClaimedAt: row.lift_claimed_at,
});

const rowColumns = `votekick_id, target_user_id, target_login, initiator_user_id, status, threshold,
  yes_votes, no_votes, ballot_revision, duration_seconds, started_at, expires_at, ended_at, lifted_at, lift_claimed_at`;

export const votekickAdmissionReasonSql = (otherwise: "busy" | "admitted"): string => `CASE
  WHEN EXISTS (SELECT 1 FROM votekicks WHERE channel_id = ? AND status = 'running') THEN 'busy'
  WHEN EXISTS (SELECT 1 FROM votekicks WHERE channel_id = ? AND ended_at > ?) THEN 'channel_cooldown'
  WHEN EXISTS (SELECT 1 FROM votekicks WHERE channel_id = ? AND target_user_id = ? AND started_at > ?) THEN 'target_cooldown'
  ELSE '${otherwise}'
END`;

const admissionCutoffs = (
  channelId: string,
  targetUserId: string,
  checkedAt: string,
  channelCooldownSeconds: number,
  targetCooldownSeconds: number,
): { values: [string, string, string, string, string, string]; channelCutoff: string; targetCutoff: string } => {
  const channelCutoff = new Date(Date.parse(checkedAt) - channelCooldownSeconds * 1000).toISOString();
  const targetCutoff = new Date(Date.parse(checkedAt) - targetCooldownSeconds * 1000).toISOString();
  return {
    values: [channelId, channelId, channelCutoff, channelId, targetUserId, targetCutoff],
    channelCutoff,
    targetCutoff,
  };
};

/** Returns the admission result from the same cooldown predicate without claiming the start. */
export const peekVotekickAdmission = async (
  db: D1Database,
  channelId: string,
  targetUserId: string,
  checkedAt: string,
  channelCooldownSeconds: number,
  targetCooldownSeconds: number,
): Promise<VotekickAdmissionResult> => {
  const { values } = admissionCutoffs(channelId, targetUserId, checkedAt, channelCooldownSeconds, targetCooldownSeconds);
  const result = await db.prepare(`SELECT ${votekickAdmissionReasonSql("admitted")} AS reason`).bind(...values)
    .first<{ reason: VotekickAdmissionResult }>();
  return result?.reason === "channel_cooldown" || result?.reason === "target_cooldown" || result?.reason === "busy"
    ? result.reason
    : "admitted";
};

export const votekickFinalizeClaimPredicate = (channelId: string, id: string): { sql: string; values: readonly string[] } => ({
  sql: "channel_id = ? AND votekick_id = ? AND status = 'running'",
  values: [channelId, id],
});

export const votekickFinishClaimPredicate = (
  channelId: string,
  id: string,
  status: Exclude<VotekickStatus, "running">,
  ballotRevision: number | null,
): { sql: string; values: readonly (string | number | null)[] } => ({
  sql: `channel_id = ? AND votekick_id = ? AND status = 'running'
    AND (? <> 'passed' OR ballot_revision <= ?)`,
  values: [channelId, id, status, ballotRevision],
});

export const votekickMarkLiftedPredicate = (channelId: string, id: string): { sql: string; values: readonly string[] } => ({
  sql: `channel_id = ? AND votekick_id = ? AND status = 'passed'
    AND lifted_at IS NULL AND target_user_id IS NOT NULL`,
  values: [channelId, id],
});

/** Checks whether the production finalization update can claim this running row. */
export const peekVotekickFinalize = async (db: D1Database, channelId: string, id: string): Promise<boolean> => {
  const votekickFinalizePredicate = votekickFinalizeClaimPredicate(channelId, id);
  const row = await db.prepare(`SELECT 1 AS claimed FROM votekicks WHERE ${votekickFinalizePredicate.sql} LIMIT 1`)
    .bind(...votekickFinalizePredicate.values).first<{ claimed: number }>();
  return row !== null;
};

/** Checks whether the production finish update can claim this running row and ballot revision. */
export const peekVotekickFinish = async (
  db: D1Database,
  channelId: string,
  id: string,
  status: Exclude<VotekickStatus, "running">,
  ballotRevision: number | null,
): Promise<boolean> => {
  const votekickFinishPredicate = votekickFinishClaimPredicate(channelId, id, status, ballotRevision);
  const row = await db.prepare(`SELECT 1 AS claimed FROM votekicks WHERE ${votekickFinishPredicate.sql} LIMIT 1`)
    .bind(...votekickFinishPredicate.values).first<{ claimed: number }>();
  return row !== null;
};

/** Reads the row that production's conditional cancellation update could claim. */
export const peekVotekickCancel = async (
  db: D1Database,
  channelId: string,
  id: string,
  now: string,
): Promise<Votekick | null> => {
  const votekickCancelPredicate = votekickFinalizeClaimPredicate(channelId, id);
  const row = await db.prepare(`SELECT ${rowColumns} FROM votekicks WHERE ${votekickCancelPredicate.sql} LIMIT 1`)
    .bind(...votekickCancelPredicate.values).first<VotekickRow>();
  return row === null ? null : { ...mapRow(row), status: "cancelled", endedAt: now };
};

/** Checks whether the production lift update can claim an unlifted passed row. */
export const peekVotekickMarkLifted = async (db: D1Database, channelId: string, id: string): Promise<boolean> => {
  const votekickLiftPredicate = votekickMarkLiftedPredicate(channelId, id);
  const row = await db.prepare(`SELECT 1 AS claimed FROM votekicks WHERE ${votekickLiftPredicate.sql} LIMIT 1`)
    .bind(...votekickLiftPredicate.values).first<{ claimed: number }>();
  return row !== null;
};

export const createVotekickRepository = (db: D1Database): VotekickRepository => ({
  async admit(channelId, input, checkedAt, channelCooldownSeconds, targetCooldownSeconds): Promise<VotekickAdmissionResult> {
    const { values: admissionValues, channelCutoff, targetCutoff } = admissionCutoffs(
      channelId,
      input.targetUserId,
      checkedAt,
      channelCooldownSeconds,
      targetCooldownSeconds,
    );
    const results = await db.batch([
      db.prepare(
        `INSERT OR IGNORE INTO votekicks
          (channel_id, votekick_id, target_user_id, target_login, initiator_user_id, status, threshold,
           yes_votes, no_votes, ballot_revision, duration_seconds, started_at, expires_at, ended_at, lifted_at)
         SELECT ?, ?, ?, ?, ?, 'running', ?, ?, 0, ?, NULL, ?, ?, NULL, NULL
          WHERE NOT EXISTS (
            SELECT 1 FROM votekicks WHERE channel_id = ? AND status = 'running'
          )
            AND NOT EXISTS (
              SELECT 1 FROM votekicks WHERE channel_id = ? AND ended_at > ?
            )
            AND NOT EXISTS (
              SELECT 1 FROM votekicks WHERE channel_id = ? AND target_user_id = ? AND started_at > ?
            )`,
      ).bind(channelId, input.id, input.targetUserId, input.targetLogin, input.initiatorUserId, input.threshold,
        input.yesVotes, input.ballotRevision, input.startedAt, input.endsAt, channelId, channelId, channelCutoff,
        channelId, input.targetUserId, targetCutoff),
      db.prepare(
        `SELECT ${votekickAdmissionReasonSql("busy")} AS reason`,
      ).bind(...admissionValues),
    ]);
    if ((results[0]?.meta.changes ?? 0) > 0) return "admitted";
    const reason = (results[1]?.results[0] as { reason?: unknown } | undefined)?.reason;
    return reason === "channel_cooldown" || reason === "target_cooldown" ? reason : "busy";
  },

  async byId(channelId, id): Promise<Votekick | null> {
    const row = await db.prepare(
      `SELECT ${rowColumns} FROM votekicks WHERE channel_id = ? AND votekick_id = ?`,
    ).bind(channelId, id).first<VotekickRow>();
    return row === null ? null : mapRow(row);
  },

  async running(channelId): Promise<Votekick | null> {
    const row = await db.prepare(
      `SELECT ${rowColumns} FROM votekicks WHERE channel_id = ? AND status = 'running' LIMIT 1`,
    ).bind(channelId).first<VotekickRow>();
    return row === null ? null : mapRow(row);
  },

  async listRecent(channelId, since): Promise<readonly Votekick[]> {
    const result = await db.prepare(
      `SELECT ${rowColumns} FROM votekicks
        WHERE channel_id = ? AND started_at >= ?
        ORDER BY started_at DESC, votekick_id DESC LIMIT 100`,
    ).bind(channelId, since).all<VotekickRow>();
    return result.results.map(mapRow);
  },

  async updateCounts(channelId, id, yesVotes, noVotes, ballotRevision): Promise<void> {
    await db.prepare(
      `UPDATE votekicks SET yes_votes = ?, no_votes = ?, ballot_revision = ?
        WHERE channel_id = ? AND votekick_id = ? AND status IN ('running', 'expired') AND ballot_revision < ?`,
    ).bind(yesVotes, noVotes, ballotRevision, channelId, id, ballotRevision).run();
  },

  async finalize(channelId, id, status, yesVotes, noVotes, ballotRevision, durationSeconds, endedAt): Promise<boolean> {
    const votekickFinalizePredicate = votekickFinalizeClaimPredicate(channelId, id);
    const result = await db.prepare(
      `UPDATE votekicks
          SET status = ?, yes_votes = ?, no_votes = ?, ballot_revision = ?, duration_seconds = ?, ended_at = ?
        WHERE ${votekickFinalizePredicate.sql}`,
    ).bind(status, yesVotes, noVotes, ballotRevision, durationSeconds, endedAt, ...votekickFinalizePredicate.values).run();
    return result.meta.changes === 1;
  },

  async finish(channelId, id, status: Exclude<VotekickStatus, "running">, yesVotes, noVotes, ballotRevision, durationSeconds, endedAt): Promise<boolean> {
    const votekickFinishPredicate = votekickFinishClaimPredicate(channelId, id, status, ballotRevision);
    const result = await db.prepare(
      `UPDATE votekicks
          SET status = ?,
              yes_votes = CASE WHEN ? IS NOT NULL AND ballot_revision < ? THEN ? ELSE yes_votes END,
              no_votes = CASE WHEN ? IS NOT NULL AND ballot_revision < ? THEN ? ELSE no_votes END,
              ballot_revision = CASE WHEN ? IS NOT NULL AND ballot_revision < ? THEN ? ELSE ballot_revision END,
              duration_seconds = ?, ended_at = ?
        WHERE ${votekickFinishPredicate.sql}`,
    ).bind(status, ballotRevision, ballotRevision, yesVotes, ballotRevision, ballotRevision, noVotes,
      ballotRevision, ballotRevision, ballotRevision, durationSeconds, endedAt, ...votekickFinishPredicate.values).run();
    return result.meta.changes > 0;
  },

  async cancel(channelId, id, now): Promise<Votekick | null> {
    const votekickCancelPredicate = votekickFinalizeClaimPredicate(channelId, id);
    const row = await db.prepare(
      `SELECT ${rowColumns} FROM votekicks WHERE ${votekickCancelPredicate.sql}`,
    ).bind(...votekickCancelPredicate.values).first<VotekickRow>();
    if (row === null) return null;
    const result = await db.prepare(
      `UPDATE votekicks SET status = 'cancelled', ended_at = ?
        WHERE ${votekickCancelPredicate.sql}`,
    ).bind(now, ...votekickCancelPredicate.values).run();
    return result.meta.changes === 0 ? null : { ...mapRow(row), status: "cancelled", endedAt: now };
  },

  async markLifted(channelId, id, now): Promise<boolean> {
    const votekickLiftPredicate = votekickMarkLiftedPredicate(channelId, id);
    const result = await db.prepare(
      `UPDATE votekicks SET lifted_at = ?, lift_claim_id = NULL, lift_claimed_at = NULL
        WHERE ${votekickLiftPredicate.sql}`,
    ).bind(now, ...votekickLiftPredicate.values).run();
    return result.meta.changes > 0;
  },
});

export const purgeExpiredVotekickUserIds = async (db: D1Database, now: string): Promise<void> => {
  const cutoff = new Date(Date.parse(now) - 14 * 24 * 60 * 60 * 1000).toISOString();
  await db.prepare(
    `UPDATE votekicks SET target_user_id = NULL, target_login = NULL, initiator_user_id = NULL
      WHERE started_at < ? AND (target_user_id IS NOT NULL OR target_login IS NOT NULL OR initiator_user_id IS NOT NULL)`,
  ).bind(cutoff).run();
};
